import { tradeCoverImage, tradesMissingCoverImage } from '@/data/constants';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * PROJECT COVER IMAGE — ONE resolver used by every surface.
 * ───────────────────────────────────────────────────────────────────────────
 * Every project must ALWAYS render a real photograph. A blank/gray tile is
 * never acceptable output, so this function can never return an empty src.
 *
 * Priority (exactly as specified by the product requirement):
 *   1. UPLOAD   — the homeowner's own photo. Index 0 of `photos` is the cover;
 *                 the poster can promote any photo to index 0 with
 *                 "Make cover" in the editor.
 *   2. TRADE    — a professional cover photo for that trade/category, resolved
 *                 through TRADE_IMAGES + TRADE_IMAGE_ALIASES so SEO slugs and
 *                 synonyms (windows-doors, pool, fencing, tile, masonry…) also
 *                 land on a real image.
 *   3. FALLBACK — the branded marketplace cover.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 2026-09-22 REGRESSION FIX — why blank cards came back
 * ───────────────────────────────────────────────────────────────────────────
 * The resolver itself was correct, but it was BRITTLE about the shape of the
 * data it was handed, so a perfectly good photo could be discarded:
 *
 *   • `photos` arriving as a JSON **string** ('["https://…"]') instead of a
 *     text[] — happens whenever a row travels through an edge function, a
 *     cached payload or a jsonb column — produced ZERO usable photos.
 *   • `photos` arriving as objects ({ url }, { publicUrl }, { path }) from an
 *     uploader payload — same outcome.
 *   • protocol-relative ('//cdn/x.jpg') and whitespace/newline-padded URLs
 *     failed the strict ^https?:// test.
 *   • only `trade` and `category` were consulted, so a row carrying the
 *     category under `trade_slug` / `category_slug` / `project_type` skipped
 *     tier 2 entirely and dropped to the branded cover.
 *
 * normalizePhotos() + tradeKeysOf() below make every one of those cases
 * resolve to a real image, and coverChainFor() hands the renderer the FULL
 * ordered candidate list so it can degrade at the network layer too.
 */

/** Branded marketplace cover — priority #3, never a gray icon. */
export const PROJECT_PLACEHOLDER =
  'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789100304318_731074ff.jpg';

export type CoverSource = 'upload' | 'trade' | 'fallback';

export interface ProjectCover {
  /** Always a usable absolute image URL. */
  src: string;
  /** True only when the image is a photo the poster actually uploaded. */
  isRealPhoto: boolean;
  /** Which tier of the priority chain produced `src`. */
  source: CoverSource;
}

export interface CoverInput {
  photos?: unknown;
  trade?: string | null;
  /** Some views carry the category under a different column. */
  category?: string | null;
  trade_slug?: string | null;
  category_slug?: string | null;
  project_type?: string | null;
}

/** Accepts only a non-empty absolute http(s) URL, so a stray '' can't win. */
const usable = (value: unknown): value is string =>
  typeof value === 'string' && /^https?:\/\/\S/i.test(value.trim());

/**
 * Coerce ONE entry of `photos` into a URL string.
 * Handles: plain strings, protocol-relative URLs, and the object shapes the
 * uploader / storage SDK emit ({ url }, { publicUrl }, { signedUrl }, { src }).
 */
const photoUrl = (entry: unknown): string | null => {
  let raw: unknown = entry;

  if (entry && typeof entry === 'object') {
    const o = entry as Record<string, unknown>;
    raw = o.url ?? o.publicUrl ?? o.public_url ?? o.signedUrl ?? o.signed_url ?? o.src ?? o.path;
  }

  if (typeof raw !== 'string') return null;

  let s = raw.trim().replace(/\s+/g, ' ');
  if (!s) return null;
  // Protocol-relative → https so it passes the absolute-URL test.
  if (s.startsWith('//')) s = `https:${s}`;
  return usable(s) ? s : null;
};

/**
 * Normalise `photos` into a clean array of loadable URLs, whatever shape the
 * caller received it in (text[], JSON string, comma list, object list, null).
 * Exported so the gallery and the audit panel use the IDENTICAL parsing.
 */
export const normalizePhotos = (input: unknown): string[] => {
  if (!input) return [];

  let list: unknown[] = [];

  if (Array.isArray(input)) {
    list = input;
  } else if (typeof input === 'string') {
    const s = input.trim();
    if (!s) return [];
    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        list = Array.isArray(parsed) ? parsed : [];
      } catch {
        list = [];
      }
    } else if (s.startsWith('{') && s.endsWith('}')) {
      // Postgres array literal that arrived as text: {"https://a","https://b"}
      list = s.slice(1, -1).split(',');
    } else {
      list = s.split(',');
    }
  } else if (typeof input === 'object') {
    list = [input];
  }

  const out: string[] = [];
  for (const entry of list) {
    const url = photoUrl(typeof entry === 'string' ? entry.replace(/^"|"$/g, '') : entry);
    if (url && !out.includes(url)) out.push(url);
  }
  return out;
};

/** Every field a row might carry its trade/category under, in priority order. */
const tradeKeysOf = (listing: CoverInput | null | undefined): (string | null | undefined)[] => [
  listing?.trade,
  listing?.trade_slug,
  listing?.category,
  listing?.category_slug,
  listing?.project_type,
];

/** The trade/category cover for a row, or null when no key matches. */
export const tradeCoverFor = (listing: CoverInput | null | undefined): string | null => {
  for (const key of tradeKeysOf(listing)) {
    const hit = tradeCoverImage(key);
    if (usable(hit)) return hit;
  }
  return null;
};

export const coverImageFor = (listing: CoverInput | null | undefined): ProjectCover => {
  // 1 — homeowner upload
  const photos = normalizePhotos(listing?.photos);
  if (photos.length > 0) return { src: photos[0], isRealPhoto: true, source: 'upload' };

  // 2 — trade / category library
  const trade = tradeCoverFor(listing);
  if (trade) return { src: trade, isRealPhoto: false, source: 'trade' };

  // 3 — branded marketplace cover
  return { src: PROJECT_PLACEHOLDER, isRealPhoto: false, source: 'fallback' };
};

/**
 * The FULL ordered candidate list for one listing:
 *   every usable upload → the trade cover → the branded cover.
 *
 * CoverImage walks this at runtime, so a deleted storage object or a CDN
 * hiccup steps to the next real photo instead of painting a broken icon.
 * Always contains at least one entry (the branded cover).
 */
export const coverChainFor = (
  listing: CoverInput | null | undefined,
): { src: string; source: CoverSource }[] => {
  const chain: { src: string; source: CoverSource }[] = normalizePhotos(listing?.photos).map(
    (src) => ({ src, source: 'upload' as const }),
  );

  const trade = tradeCoverFor(listing);
  if (trade) chain.push({ src: trade, source: 'trade' });
  chain.push({ src: PROJECT_PLACEHOLDER, source: 'fallback' });

  return chain.filter((c, i) => chain.findIndex((x) => x.src === c.src) === i);
};

/** Convenience for schema.org / og:image where only a URL is needed. */
export const coverUrlFor = (listing: CoverInput | null | undefined): string =>
  coverImageFor(listing).src;

/**
 * Health check used by the admin launch checks: any trade listed here would
 * render the generic marketplace cover instead of a category photo.
 */
export const coverCoverageReport = () => {
  const missing = tradesMissingCoverImage();
  return { ok: missing.length === 0, missingTrades: missing };
};
